import type { ActionTree } from 'vuex'
import type { CampaignState } from './types'
import type { RootState } from '@/store/modules/types'
import { normalizeCampaignData } from '@/utils/normalizeData'
import { campaignStatusClause, defaultSelectFields } from '@/api/campaigns'
import { getContext } from '@/utils/general'

const UNIQUE_URL_EXISTS_MESSAGE
  = 'Unique URL already exist, please use another URL'

function getCampaignErrorMessage(error: unknown, fallbackMessage: string) {
  const errorData = error as {
    response?: {
      data?: {
        ['internal-code']?: string;
        internalCode?: string;
        message?: {
          summary?: string;
          details?: string;
        };
      };
    };
    message?: {
      summary?: string;
    };
  }
  const internalErrorCode
    = errorData?.response?.data?.internalCode
      || errorData?.response?.data?.['internal-code']
  const internalErrorSummary = errorData?.response?.data?.message?.summary
  const internalErrorDetails = errorData?.response?.data?.message?.details
  const rawMessage = internalErrorSummary || internalErrorDetails || ''

  if (
    internalErrorCode === 'record-exists'
    || /record\s+exists?/i.test(rawMessage)
  ) {
    return UNIQUE_URL_EXISTS_MESSAGE
  }

  return internalErrorSummary || errorData?.message?.summary || fallbackMessage
}

export const campaignActions: ActionTree<CampaignState, RootState> = {
  async FETCH_CAMPAIGNS(
    { state, commit, rootState },
    {
      $select = 'name,oid,title,status',
      $expand = 'all',
      $top = 50,
      $orderby = 'name asc',
      searchString = '',
    },
  ) {
    if (!rootState.authModule.account) {
      return null
    }
    const { promoterOid } = rootState.authModule.account
    const { $api, $arNotification } = getContext()

    try {
      commit('SET_IS_FETCHING_CAMPAIGNS', true)
      const data = await $api.campaign.search(promoterOid, {
        top: $top,
        skip: 0,
        searchString: searchString || null,
        select: $select,
        orderby: $orderby,
        type: state.campaignsTypeFilter,
      })

      commit('SET_CAMPAIGNS', data)
      return true
    }
    catch (error) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: 'Failed to fetch campaigns',
      })
      return false
    }
    finally {
      commit('SET_IS_FETCHING_CAMPAIGNS', false)
    }
  },

  async FETCH_CURRENT_CAMPAIGN({ rootState, commit }, oid: number) {
    if (!rootState.authModule.account) {
      return null
    }
    const { promoterOid } = rootState.authModule.account
    const { $api, $arNotification } = getContext()

    // A campaign fetched immediately after creation can briefly 404 while the
    // read path catches up with the write (backend eventual consistency).
    // Retry a couple of times before giving up, rather than bouncing the user
    // straight back to the campaigns list on the first transient 404.
    const maxAttempts = 3
    const retryDelayMs = 1000

    try {
      commit('SET_IS_FETCHING_CAMPAIGN', true)

      let data
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          data = await $api.campaign.get(
            promoterOid,
            oid,
            'name,type,messageListOid,notifyDate,startDate,endDate,timeZone,urlSlug,eventOid,event,rewards,presentation,resources,resourceOids,registrations,settings,socialActions,defaults,subscriptionLevel',
          )
          break
        }
        catch (error) {
          const status = (error as { status?: number })?.status
          if (status !== 404 || attempt === maxAttempts) {
            throw error
          }
          await new Promise(resolve => setTimeout(resolve, retryDelayMs))
        }
      }

      // const normalizedData = await normalizeCampaignData(data)
      commit('SET_CURRENT_CAMPAIGN', data)

      return data
    }
    catch (error) {
      console.error(error)

      // @ts-ignore
      $arNotification.push({
        type: 'error',
        message: 'Failed to fetch campaign',
      })
      return false
    }
    finally {
      commit('SET_IS_FETCHING_CAMPAIGN', false)
    }
  },

  async FETCH_CAMPAIGN_REGISTRATION_STATS(
    { rootState, commit, dispatch },
    oid: number,
  ) {
    if (!rootState.authModule.account) {
      return null
    }
    const { promoterOid } = rootState.authModule.account
    const { $api, $arNotification } = getContext()

    if (isNaN(oid)) {
      console.error(
        'FETCH_CAMPAIGN_REGISTRATION_STATS called with NaN campaignOid',
      )
      return
    }

    try {
      commit('SET_IS_FETCHING_CAMPAIGN_REGISTRATION_STATS', true)
      const data = await $api.campaign.fetchCampaignRegistrationStats(
        promoterOid,
        oid,
      )

      // Also fetch the time series data
      const seriesData = await dispatch(
        'FETCH_CAMPAIGN_REGISTRATIONS_SERIES',
        oid,
      )

      // Combine the data
      if (seriesData) {
        data.registrationSeries = seriesData
      }

      commit('SET_CURRENT_CAMPAIGN_REGISTRATION_STATS', data)
      return data
    }
    catch (error) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: 'Failed to fetch campaign registrations',
      })
    }
    finally {
      commit('SET_IS_FETCHING_CAMPAIGN_REGISTRATION_STATS', false)
    }
  },

  async FETCH_CAMPAIGN_REGISTRATIONS_SERIES({ rootState }, oid: number) {
    if (!rootState.authModule.account) {
      return null
    }
    const { promoterOid } = rootState.authModule.account
    const { $api } = getContext()

    if (isNaN(oid)) {
      console.error(
        'FETCH_CAMPAIGN_REGISTRATIONS_SERIES called with NaN campaignOid',
      )
      return null
    }

    try {
      const data = await $api.campaign.fetchCampaignRegistrationsSeries(
        promoterOid,
        oid,
      )
      return data
    }
    catch (error) {
      console.error('Error fetching campaign registration series:', error)
      return null
    }
  },

  // Its only caller (the paginated campaigns list) always replaces the current
  // page rather than appending to it, so this only ever reloads -- unlike
  // FETCH_CAMPAIGNS_BY_OID/FETCH_CAMPAIGNS_BY_EVENT_OID below, it has no
  // append mode.
  async FETCH_MORE_CAMPAIGNS(
    { state, commit, rootState },
    {
      top = 12,
      skip = 0,
      searchString = '',
      status = 'all',
      select = defaultSelectFields,
      orderby = undefined,
      signal = undefined,
    },
  ) {
    if (!rootState.authModule.account) {
      return null
    }
    const { promoterOid } = rootState.authModule.account
    const { $api, $axios, $arNotification } = getContext()

    try {
      commit('SET_IS_FETCHING_CAMPAIGNS', true)
      const data = await $api.campaign.search(promoterOid, {
        top,
        skip,
        searchString,
        select,
        orderby,
        type: state.campaignsTypeFilter,
        status,
        signal,
      })

      commit('SET_CAMPAIGNS', data)
    }
    catch (error) {
      // A superseding fetchCampaigns() call aborts whatever was still in
      // flight -- that's expected, not a failure, so it shouldn't commit
      // nothing and stay silent rather than surface an error toast.
      if ($axios.isCancel(error)) {
        return
      }
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: 'Failed to fetch campaigns',
      })
    }
    finally {
      commit('SET_IS_FETCHING_CAMPAIGNS', false)
    }
  },

  async FETCH_CAMPAIGNS_COUNT(
    { state, commit, rootState },
    { searchString = '', status = 'all', signal = undefined } = {},
  ) {
    if (!rootState.authModule.account) {
      return null
    }
    const { promoterOid } = rootState.authModule.account
    const { $api, $axios } = getContext()

    // Mirrors the filter semantics of $api.campaign.search(): a type-specific
    // clause (when the secondary nav narrows to one data-capture sub-type) plus
    // the search-box ILIKE clause and the status clause. fetchCount adds the opt-in/rsvp exclusion
    // itself, so it isn't duplicated here.
    let filter = state.campaignsTypeFilter && state.campaignsTypeFilter !== 'all'
      ? `type = "${state.campaignsTypeFilter}"`
      : null
    if (searchString) {
      const searchClause = `name ILIKE "%${searchString}%"`
      filter = filter ? `${filter} AND ${searchClause}` : searchClause
    }
    const statusClause = campaignStatusClause(status)
    if (statusClause) {
      filter = filter ? `${filter} AND ${statusClause}` : statusClause
    }

    try {
      const data = await $api.campaign.fetchCount(promoterOid, { filter, signal })
      commit('SET_TOTAL_CAMPAIGNS_COUNT', data?.count || 0)
    }
    catch (error) {
      // A cancelled count (superseded by a newer search/filter change) must not
      // zero out the total -- that would flash the pager to "no results" while
      // the request that will actually update it is still in flight.
      if ($axios.isCancel(error)) {
        return
      }
      console.error('Error fetching campaigns count:', error)
      commit('SET_TOTAL_CAMPAIGNS_COUNT', 0)
    }
  },

  async FETCH_CAMPAIGNS_BY_OID(
    { state, commit, rootState },
    { oids = [], reload = false },
  ) {
    if (!rootState.authModule.account) {
      return null
    }
    const { promoterOid } = rootState.authModule.account
    const { $api, $arNotification } = getContext()

    try {
      commit('SET_IS_FETCHING_CAMPAIGNS', true)
      const data = await $api.campaign.fetchByOids(promoterOid, oids)

      if (reload) {
        commit('SET_CAMPAIGNS', data)
      }
      else {
        commit('CONCAT_CAMPAIGNS', data)
      }
    }
    catch (error) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: 'Failed to fetch campaigns',
      })
    }
    finally {
      commit('SET_IS_FETCHING_CAMPAIGNS', false)
    }
  },

  async FETCH_CAMPAIGNS_BY_EVENT_OID(
    { state, commit, rootState },
    { eventOid, reload = false },
  ) {
    if (!rootState.authModule.account) {
      return null
    }
    const { promoterOid } = rootState.authModule.account
    const { $axios, $arNotification } = getContext()

    try {
      commit('SET_IS_FETCHING_CAMPAIGNS', true)
      const { data } = await $axios.get(
        `/promoter/${promoterOid}/campaign?$select=name,type,notifyDate,startDate,endDate,event,timeZone,presentation,urlSlug,summaryStatsSnapshot,socialActions,registrations,settings,resources,subscriptionLevel&$filter=eventOid=${eventOid} AND type != rsvp`,
      )

      if (reload) {
        commit('SET_CAMPAIGNS', data)
      }
      else {
        commit('CONCAT_CAMPAIGNS', data)
      }
    }
    catch (error) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: 'Failed to fetch campaigns',
      })
    }
    finally {
      commit('SET_IS_FETCHING_CAMPAIGNS', false)
    }
  },

  async PUBLISH_CAMPAIGN({ rootState, commit }, campaignData: any) {
    if (!rootState.authModule.account) {
      return null
    }
    const { promoterOid } = rootState.authModule.account
    const { $axios, $arNotification } = getContext()

    delete campaignData.resources
    delete campaignData.availableActions
    delete campaignData.preview

    try {
      commit('SET_IS_CREATING_CAMPAIGN', true)

      if (!campaignData.eventOid) {
        // when no event is passed, create a fake one to please the NOT NULL constraint on Campaign table
        const eventData = {
          startDate: new Date().toISOString(),
          name: 'auto-created::' + campaignData.name,
          meta: {
            synch: {},
          },
          timeZone: 'Australia/Sydney',
          presentation: {
            liveStreamUrl: null,
            website: null,
            settings: {},
            suggestedAudience: null,
          },
          resourceOids: [],
          endDate: null,
          location: 'test',
          capacity: null,
          provider: null,
          userDefined: false,
        }
        const {
          data: { oid: eventOid },
        } = await $axios.post(`/promoter/${promoterOid}/new-event`, eventData)
        campaignData.eventOid = eventOid
      }

      const response = await $axios.post(
        `/promoter/${promoterOid}/campaign`,
        campaignData,
      )

      $arNotification.push({
        type: 'success',
        message: 'Campaign published successfully',
      })

      return response.data
    }
    catch (error) {
      console.error('Error publishing campaign:', error)
      const errorMessage = getCampaignErrorMessage(
        error,
        'Failed to publish campaign',
      )
      $arNotification.push({
        type: 'error',
        message: errorMessage,
      })
      return false
    }
    finally {
      commit('SET_IS_CREATING_CAMPAIGN', false)
    }
  },

  async UPDATE_CAMPAIGN({ rootState, commit }, campaignData: any) {
    if (!rootState.authModule.account) {
      return null
    }
    const { promoterOid } = rootState.authModule.account
    const { $axios, $arNotification } = getContext()

    delete campaignData.resources
    delete campaignData.availableActions
    delete campaignData.preview

    try {
      commit('SET_IS_CREATING_CAMPAIGN', true)

      delete campaignData.event
      delete campaignData.sysCtime
      delete campaignData.sysMtime
      campaignData.oid = Number(campaignData.oid)

      // Campaigns require a non-null eventOid due to a NOT NULL constraint on the
      // campaign–event relationship. If the user removed the linked event (or none
      // was ever selected), we create a lightweight placeholder event here before
      // patching — mirroring the same behavior in PUBLISH_CAMPAIGN. The event is
      // marked userDefined: false and prefixed "auto-created::" so the UI can
      // detect it and prompt the user to link a real event later.
      if (!campaignData.eventOid) {
        const { data: { oid: eventOid } } = await $axios.post(`/promoter/${promoterOid}/new-event`, {
          startDate: new Date().toISOString(),
          name: 'auto-created::' + campaignData.name,
          meta: { synch: {} },
          timeZone: 'Australia/Sydney',
          presentation: { liveStreamUrl: null, website: null, settings: {}, suggestedAudience: null },
          resourceOids: [],
          endDate: null,
          location: 'test',
          capacity: null,
          provider: null,
          userDefined: false,
        })
        campaignData.eventOid = eventOid
      }

      const response = await $axios.patch(
        `/promoter/${promoterOid}/campaign/${campaignData.oid}`,
        campaignData,
      )

      $arNotification.push({
        type: 'success',
        message: 'Campaign updated successfully',
      })

      return true
    }
    catch (error) {
      console.error('Error updating campaign:', error)
      const errorMessage = getCampaignErrorMessage(
        error,
        'Failed to update campaign',
      )
      $arNotification.push({
        type: 'error',
        message: errorMessage,
      })
      return false
    }
    finally {
      commit('SET_IS_CREATING_CAMPAIGN', false)
    }
  },

  async DELETE_CAMPAIGN({ rootState, commit }, campaignOid: number) {
    if (!rootState.authModule.account) {
      return null
    }
    const { promoterOid } = rootState.authModule.account
    const { $axios, $arNotification } = getContext()

    try {
      commit('SET_IS_UPDATING_CAMPAIGN', true)

      await $axios.delete(`/promoter/${promoterOid}/campaign/${campaignOid}`)

      $arNotification.push({
        type: 'success',
        message: 'Campaign deleted successfully',
      })

      return campaignOid
    }
    catch (error) {
      console.error('Error deleting campaign:', error)
      $arNotification.push({
        type: 'error',
        message: 'Failed to delete campaign',
      })
      return false
    }
    finally {
      commit('SET_IS_UPDATING_CAMPAIGN', false)
    }
  },
}
